import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Plus, X, ExternalLink, Eye, EyeOff, RefreshCw, MoreVertical, AlertTriangle, Pencil, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Mixed-case letters + digits, 8 chars. Excludes ambiguous chars (0/O/1/l/I).
function generateInitialPassword() {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  const arr = new Uint32Array(8);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  return Array.from(arr, (n) => alphabet[n % alphabet.length]).join("");
}

const STATUS_TABS = [
  { key: "active", label: "Active" },
  { key: "on_leave", label: "On Leave" },
  { key: "resigned", label: "Resigned" },
];

const STATUS_BADGE = {
  active:   "bg-green-50 text-green-700 border-green-200",
  on_leave: "bg-amber-50 text-amber-800 border-amber-200",
  resigned: "bg-neutral-100 text-neutral-500 border-neutral-200",
};

const STATUS_LABEL = { active: "ACTIVE", on_leave: "ON LEAVE", resigned: "RESIGNED" };

export default function Employees({ workforceType = null }) {
  const { user } = useAuth();
  const canEdit = ["hr", "super_admin"].includes(user.system_role);
  const isSuperAdmin = user.system_role === "super_admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const unassessedFilter = searchParams.get("unassessed") === "1";
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [postAddPrompt, setPostAddPrompt] = useState(null); // { employee } — appears after successful add
  const [editing, setEditing] = useState(null);   // employee object being edited
  const [resetting, setResetting] = useState(null); // employee whose password is being reset
  const [deleting, setDeleting] = useState(null); // employee being hard-deleted (super_admin only)
  const [activeTab, setActiveTab] = useState("active");
  const [confirm, setConfirm] = useState(null); // { employee, nextStatus }
  /** Roles that have ≥1 skill in the competency map. Loaded once on mount. */
  const [rolesWithSkills, setRolesWithSkills] = useState(new Set());

  const load = () => {
    if (unassessedFilter && canEdit) {
      const params = workforceType ? { workforce_type: workforceType } : {};
      return api.get("/dashboard/unassessed-employees", { params })
        .then((r) => setList(r.data.employees || []));
    }
    const params = workforceType ? { workforce_type: workforceType } : {};
    return api.get("/employees", { params }).then((r) => setList(r.data));
  };
  useEffect(() => { load(); }, [workforceType, unassessedFilter, canEdit]);

  // Load competency map once to determine which employees have no requirements.
  useEffect(() => {
    api.get("/competency-map").then((r) => {
      const withSkills = new Set(
        r.data.filter((c) => c.skills && Object.keys(c.skills).length > 0).map((c) => c.role)
      );
      setRolesWithSkills(withSkills);
    });
  }, []);

  /** True when none of the employee's roles have any skills in the competency map. */
  const hasNoRequirements = (emp) =>
    (emp.roles || []).length > 0 && (emp.roles || []).every((r) => !rolesWithSkills.has(r));

  const clearUnassessedFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("unassessed");
    setSearchParams(next, { replace: true });
  };

  const trackLabel = workforceType === "worker" ? "Operators" : workforceType === "staff" ? "Staff" : null;
  const pageTitle  = trackLabel ? `Employee List — ${trackLabel}` : "Employees";
  const pageEyebrow = trackLabel ? `People · ${trackLabel}` : "People";

  // Resolve effective status (fallback to is_active for any legacy rows still without it)
  const effectiveStatus = (e) => e.status || (e.is_active ? "active" : "resigned");

  const counts = useMemo(() => ({
    active:   list.filter((e) => effectiveStatus(e) === "active").length,
    on_leave: list.filter((e) => effectiveStatus(e) === "on_leave").length,
    resigned: list.filter((e) => effectiveStatus(e) === "resigned").length,
  }), [list]);

  const filtered = list
    .filter((e) => effectiveStatus(e) === activeTab)
    .filter((e) =>
      !search ||
      e.name.toLowerCase().includes(search.toLowerCase()) ||
      e.email.toLowerCase().includes(search.toLowerCase()) ||
      e.department.toLowerCase().includes(search.toLowerCase())
    );

  const requestStatusChange = (employee, nextStatus) => {
    if (effectiveStatus(employee) === nextStatus) return;
    if (nextStatus === "resigned") {
      setConfirm({ employee, nextStatus });
    } else {
      void applyStatusChange(employee, nextStatus);
    }
  };

  const applyStatusChange = async (employee, nextStatus) => {
    try {
      const res = await api.patch(`/employees/${employee.employee_id}/status`, { status: nextStatus });
      const cancelled = res.data?.cancelled_assessments || 0;
      const baseMsg = `${employee.name} marked as ${STATUS_LABEL[nextStatus]}`;
      toast.success(cancelled > 0
        ? `${baseMsg}. ${cancelled} pending test${cancelled > 1 ? "s" : ""} automatically cancelled.`
        : baseMsg
      );
      setConfirm(null);
      await load();
      // Auto-jump to the tab where the row now lives so the user sees the change.
      setActiveTab(nextStatus);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change status");
    }
  };

  return (
    <div data-testid="employees-page">
      <PageHeader
        eyebrow={pageEyebrow}
        title={pageTitle}
        subtitle={trackLabel ? `Manage ${trackLabel === "Operators" ? "operator" : "staff"} records, roles, and active status.` : "Manage workforce records, roles, and active status."}
        breadcrumbs={[{ label: "Home", to: "/" }, { label: pageTitle }]}
        action={
          canEdit && (
            <button onClick={() => setShowAdd(true)} data-testid="add-employee-button" className="inline-flex items-center gap-1.5 bg-[#C41E1E] hover:bg-[#A31818] text-white text-sm rounded-sm px-3 py-2">
              <Plus size={14} /> Add employee
            </button>
          )
        }
      />
      <div className="px-4 md:px-10 py-8 space-y-4">
        {unassessedFilter && canEdit && (
          <div
            className="flex items-center justify-between rounded-sm border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900"
            data-testid="unassessed-filter-banner"
          >
            <span>
              <strong>Filtered:</strong> employees with no assessments yet ({list.length})
            </span>
            <button
              type="button"
              onClick={clearUnassessedFilter}
              data-testid="unassessed-clear-filter"
              className="text-xs font-medium underline hover:text-amber-950"
            >
              Clear filter
            </button>
          </div>
        )}
        {/* Status tabs */}
        <div className="flex gap-1 border-b border-[#E5E5E5]" role="tablist">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              data-testid={`status-tab-${t.key}`}
              aria-selected={activeTab === t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === t.key ? "border-[#C41E1E] text-[#C41E1E]" : "border-transparent text-[#666666] hover:text-[#0D0D0D]"
              }`}
            >
              {t.label}
              <span className="ml-2 text-[10px] font-mono text-[#666666]">{counts[t.key]}</span>
            </button>
          ))}
        </div>

        <input
          data-testid="employee-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, department…"
          className="w-full md:max-w-md border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm"
        />
        <div className="bc-card overflow-x-auto" data-testid="employees-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F9F9F9] border-b border-[#E5E5E5]">
                <th className="text-left px-5 py-2.5 bc-label">#</th>
                <th className="text-left px-5 py-2.5 bc-label">Name</th>
                <th className="text-left px-5 py-2.5 bc-label">Email</th>
                <th className="text-left px-5 py-2.5 bc-label">Department</th>
                <th className="text-left px-5 py-2.5 bc-label">Roles</th>
                <th className="text-left px-5 py-2.5 bc-label">System role</th>
                <th className="text-left px-5 py-2.5 bc-label">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="8" className="px-5 py-10 text-center text-sm text-[#666666]">
                  No employees in <strong>{STATUS_TABS.find((t) => t.key === activeTab).label}</strong>.
                </td></tr>
              )}
              {filtered.map((e) => {
                const status = effectiveStatus(e);
                return (
                  <tr key={e.employee_id} className="border-b border-[#E5E5E5] bc-row-hover" data-testid={`employee-row-${e.employee_id}`} data-status={status}>
                    <td className="px-5 py-2.5 font-mono text-xs">{e.employee_id}</td>
                    <td className="px-5 py-2.5 font-medium">
                      <span className="inline-flex items-center gap-2">
                        {e.name}
                        <WorkforceBadge type={e.workforce_type} testid={`workforce-badge-${e.employee_id}`} />
                      </span>
                    </td>
                    <td className="px-5 py-2.5 text-xs font-mono text-[#666666]">{e.email}</td>
                    <td className="px-5 py-2.5">{e.department}</td>
                    <td className="px-5 py-2.5 text-xs">
                      <span>{(e.roles || []).join(", ")}</span>
                      {hasNoRequirements(e) && (
                        <span
                          className="ml-1.5 text-[10px] text-[#999999] border border-[#E5E5E5] rounded-sm px-1.5 py-0.5 whitespace-nowrap"
                          data-testid={`no-requirements-badge-${e.employee_id}`}
                          title="This employee's role has no competency requirements defined in the competency map"
                        >
                          No requirements
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-2.5"><span className="text-xs uppercase tracking-wide font-medium">{(e.system_role || "—").replace("_", " ")}</span></td>
                    <td className="px-5 py-2.5">
                      <span
                        data-testid={`status-badge-${e.employee_id}`}
                        className={`text-[10px] px-2 py-0.5 rounded-sm border ${STATUS_BADGE[status]}`}
                      >
                        {STATUS_LABEL[status]}
                      </span>
                    </td>
                    <td className="px-5 py-2.5 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-3">
                        <Link to={`/competency-card/${e.employee_id}`} data-testid={`view-card-${e.employee_id}`} className="text-xs text-[#C41E1E] font-medium inline-flex items-center gap-1">
                          Card <ExternalLink size={11} />
                        </Link>
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => setEditing(e)}
                            data-testid={`edit-employee-${e.employee_id}`}
                            className="text-xs text-[#666666] hover:text-[#0D0D0D] border border-[#E5E5E5] hover:border-[#0D0D0D] rounded-sm px-2 py-1 inline-flex items-center gap-1"
                          >
                            <Pencil size={12} /> Edit
                          </button>
                        )}
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => setResetting(e)}
                            data-testid={`reset-password-${e.employee_id}`}
                            className="text-xs text-[#666666] hover:text-[#C41E1E] border border-[#E5E5E5] hover:border-[#C41E1E] rounded-sm px-2 py-1 inline-flex items-center gap-1"
                          >
                            <KeyRound size={12} /> Reset Password
                          </button>
                        )}
                        {isSuperAdmin && e.employee_id !== user.employee_id && (
                          <button
                            type="button"
                            onClick={() => setDeleting(e)}
                            data-testid={`delete-employee-${e.employee_id}`}
                            title="Permanently delete this employee and all linked records"
                            className="text-xs text-[#C41E1E] hover:text-white hover:bg-[#C41E1E] border border-[#C41E1E]/40 hover:border-[#C41E1E] rounded-sm px-2 py-1 inline-flex items-center gap-1"
                          >
                            <Trash2 size={12} /> Delete
                          </button>
                        )}
                        {canEdit && (
                          <StatusMenu
                            employee={e}
                            currentStatus={status}
                            onSelect={(nextStatus) => requestStatusChange(e, nextStatus)}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && (
        <AddEmployeeModal
          onClose={() => setShowAdd(false)}
          onDone={(createdEmployee) => {
            setShowAdd(false);
            load();
            // Only prompt for TNI assignment when the new employee has roles
            // that could map to competencies — otherwise there'd be nothing to assign.
            if (createdEmployee && (createdEmployee.roles || []).length > 0) {
              setPostAddPrompt({ employee: createdEmployee });
            } else {
              toast.success("Employee added successfully");
            }
          }}
        />
      )}
      {postAddPrompt && (
        <PostAddAssignPrompt
          employee={postAddPrompt.employee}
          onClose={() => { setPostAddPrompt(null); load(); }}
        />
      )}
      {editing && (
        <EditEmployeeModal
          employee={editing}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); load(); }}
        />
      )}
      {resetting && (
        <ResetPasswordModal
          employee={resetting}
          onClose={() => setResetting(null)}
        />
      )}
      {deleting && (
        <DeleteEmployeeModal
          employee={deleting}
          onClose={() => setDeleting(null)}
          onDone={() => { setDeleting(null); load(); }}
        />
      )}
      {confirm && (
        <ConfirmResignModal
          employee={confirm.employee}
          onCancel={() => setConfirm(null)}
          onConfirm={() => applyStatusChange(confirm.employee, "resigned")}
        />
      )}
    </div>
  );
}

function StatusMenu({ employee, currentStatus, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid={`change-status-${employee.employee_id}`}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-xs text-[#666666] hover:text-[#0D0D0D] border border-[#E5E5E5] hover:border-[#0D0D0D] rounded-sm px-2 py-1"
      >
        Change <MoreVertical size={12} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-44 bg-white border border-[#E5E5E5] rounded-sm shadow-md z-20 py-1"
             data-testid={`status-menu-${employee.employee_id}`}>
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              data-testid={`set-status-${t.key}-${employee.employee_id}`}
              disabled={t.key === currentStatus}
              onClick={() => { setOpen(false); onSelect(t.key); }}
              className={`block w-full text-left text-xs px-3 py-2 ${
                t.key === currentStatus
                  ? "text-[#999] bg-[#FAFAFA] cursor-default"
                  : "hover:bg-[#F5F5F5]"
              }`}
            >
              Mark as {t.label}
              {t.key === currentStatus && <span className="ml-1 text-[10px]">(current)</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ConfirmResignModal({ employee, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-50" onClick={onCancel}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-[#E5E5E5] rounded-sm w-full max-w-md p-6"
        data-testid="confirm-resign-modal"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-red-50 text-[#C41E1E] flex items-center justify-center rounded-sm shrink-0">
            <AlertTriangle size={18} />
          </div>
          <div>
            <h3 className="font-heading text-base font-semibold">Mark as resigned?</h3>
            <p className="text-sm text-[#1A1A1A] mt-2">
              This will hide <strong>{employee.name}</strong> from all active views. Their records will be preserved. Continue?
            </p>
            <ul className="text-xs text-[#666666] mt-3 space-y-1 list-disc pl-4">
              <li>Hidden from TNI Matrix, Skill Matrix, and Assign Test selector.</li>
              <li>Removed from training nominee auto-suggestions.</li>
              <li>Competency card and historical training attendance remain accessible.</li>
            </ul>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onCancel}
            data-testid="cancel-resign"
            className="text-sm px-4 py-2 border border-[#E5E5E5] hover:border-[#0D0D0D] rounded-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            data-testid="confirm-resign"
            className="text-sm px-4 py-2 bg-[#C41E1E] hover:bg-[#A31818] text-white rounded-sm font-medium"
          >
            Mark as Resigned
          </button>
        </div>
      </div>
    </div>
  );
}

function AddEmployeeModal({ onClose, onDone }) {
  const [cmap, setCmap] = useState([]);
  useEffect(() => { api.get("/competency-map").then((r) => setCmap(r.data)); }, []);
  const [form, setForm] = useState({
    employee_id: "",
    name: "",
    email: "",
    department: "Production",
    roles: [],
    joining_date: new Date().toISOString().slice(0, 10),
    system_role: "employee",
    password: generateInitialPassword(),
  });
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const regeneratePassword = () => {
    setForm((f) => ({ ...f, password: generateInitialPassword() }));
  };

  const toggleRole = (r) => {
    setForm((f) => ({ ...f, roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const res = await api.post("/employees", { ...form, employee_id: parseInt(form.employee_id) });
      onDone(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-50" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border border-[#E5E5E5] rounded-sm w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="add-employee-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold">Add employee</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="bc-label block mb-1">Employee ID</label><input required type="number" data-testid="emp-id" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" /></div>
          <div><label className="bc-label block mb-1">Department</label><input required value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" /></div>
        </div>
        <div><label className="bc-label block mb-1">Name</label><input required data-testid="emp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" /></div>
        <div><label className="bc-label block mb-1">Email</label><input required type="email" data-testid="emp-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" /></div>
        <div>
          <label className="bc-label block mb-1">Roles</label>
          <div className="max-h-32 overflow-y-auto border border-[#E5E5E5] p-2 rounded-sm">
            {cmap.map((c) => (
              <label key={c.role} className="flex items-center gap-2 text-sm py-1">
                <input type="checkbox" checked={form.roles.includes(c.role)} onChange={() => toggleRole(c.role)} className="accent-[#C41E1E]" />
                {c.role}
              </label>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="bc-label block mb-1">System role</label>
            <select value={form.system_role} onChange={(e) => setForm({ ...form, system_role: e.target.value })} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" data-testid="emp-system-role">
              <option value="employee">Employee</option>
              <option value="hr">HR</option>
              <option value="quality">Quality</option>
              <option value="management">Management</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>
          <div>
            <label className="bc-label block mb-1">Workforce type</label>
            <select
              value={form.workforce_type || ""}
              onChange={(e) => setForm({ ...form, workforce_type: e.target.value || null })}
              className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm"
              data-testid="emp-workforce-type"
            >
              <option value="">Auto-classify from department</option>
              <option value="staff">Staff</option>
              <option value="worker">Operator</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="bc-label block mb-1">Joining date</label><input type="date" required value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" /></div>
        </div>
        <div>
          <label className="bc-label block mb-1">Initial password</label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 pr-20 text-sm font-mono"
              data-testid="emp-password"
            />
            <div className="absolute inset-y-0 right-1 flex items-center gap-0.5">
              <button
                type="button"
                onClick={regeneratePassword}
                data-testid="regenerate-password"
                title="Generate a new random password"
                className="p-1.5 text-[#666666] hover:text-[#0D0D0D] rounded-sm"
              >
                <RefreshCw size={14} />
              </button>
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                data-testid="toggle-password-visibility"
                title={showPassword ? "Hide password" : "Show password"}
                className="p-1.5 text-[#666666] hover:text-[#0D0D0D] rounded-sm"
              >
                {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div className="text-[10px] text-[#666666] mt-1">Auto-generated. Share securely with the employee — they should change it on first login.</div>
        </div>
        {err && <div className="text-sm text-[#C41E1E] bg-red-50 px-3 py-2 rounded-sm border border-red-200">{err}</div>}
        <button type="submit" disabled={busy} data-testid="submit-employee" className="w-full bg-[#C41E1E] hover:bg-[#A31818] text-white text-sm rounded-sm py-2.5 font-medium disabled:opacity-60">
          {busy ? "Creating…" : "Create employee"}
        </button>
      </form>
    </div>
  );
}


function EditEmployeeModal({ employee, onClose, onDone }) {
  const [cmap, setCmap] = useState([]);
  useEffect(() => { api.get("/competency-map").then((r) => setCmap(r.data)); }, []);
  const [form, setForm] = useState({
    name: employee.name || "",
    email: employee.email || "",
    department: employee.department || "",
    roles: Array.isArray(employee.roles) ? [...employee.roles] : [],
    system_role: employee.system_role || "employee",
    status: employee.status || (employee.is_active ? "active" : "resigned"),
    workforce_type: employee.workforce_type || "staff",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const toggleRole = (r) => {
    setForm((f) => ({ ...f, roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      // Send only fields that actually changed — keeps the audit trail clean.
      const payload = {};
      const original = {
        name: employee.name || "",
        email: employee.email || "",
        department: employee.department || "",
        roles: Array.isArray(employee.roles) ? [...employee.roles].sort() : [],
        system_role: employee.system_role || "employee",
        status: employee.status || (employee.is_active ? "active" : "resigned"),
        workforce_type: employee.workforce_type || "staff",
      };
      if (form.name !== original.name) payload.name = form.name;
      if (form.email.toLowerCase() !== original.email.toLowerCase()) payload.email = form.email.toLowerCase();
      if (form.department !== original.department) payload.department = form.department;
      if (JSON.stringify([...form.roles].sort()) !== JSON.stringify(original.roles)) payload.roles = form.roles;
      if (form.system_role !== original.system_role) payload.system_role = form.system_role;
      if (form.status !== original.status) payload.status = form.status;
      if (form.workforce_type !== original.workforce_type) payload.workforce_type = form.workforce_type;

      if (Object.keys(payload).length === 0) {
        toast.info("No changes to save");
        onClose();
        return;
      }
      const res = await api.patch(`/employees/${employee.employee_id}`, payload);
      const cancelled = res?.data?.cancelled_assessments || 0;
      const baseMsg = `${form.name} updated`;
      toast.success(cancelled > 0
        ? `${baseMsg}. ${cancelled} pending test${cancelled > 1 ? "s" : ""} automatically cancelled.`
        : baseMsg
      );
      onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-50" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white border border-[#E5E5E5] rounded-sm w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        data-testid="edit-employee-modal"
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg font-semibold">Edit employee</h3>
            <div className="text-xs text-[#666666] mt-0.5">Employee ID <span className="font-mono">#{employee.employee_id}</span> · cannot be changed.</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div>
          <label className="bc-label block mb-1">Name</label>
          <input
            required
            data-testid="edit-emp-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="bc-label block mb-1">Email</label>
          <input
            required
            type="email"
            data-testid="edit-emp-email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="bc-label block mb-1">Department</label>
          <input
            required
            data-testid="edit-emp-department"
            value={form.department}
            onChange={(e) => setForm({ ...form, department: e.target.value })}
            className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="bc-label block mb-1">Roles</label>
          <div className="max-h-32 overflow-y-auto border border-[#E5E5E5] p-2 rounded-sm" data-testid="edit-emp-roles">
            {cmap.length === 0 && <div className="text-xs text-[#666666] italic">Loading roles…</div>}
            {cmap.map((c) => (
              <label key={c.role} className="flex items-center gap-2 text-sm py-1">
                <input
                  type="checkbox"
                  checked={form.roles.includes(c.role)}
                  onChange={() => toggleRole(c.role)}
                  data-testid={`edit-emp-role-${c.role}`}
                  className="accent-[#C41E1E]"
                />
                {c.role}
              </label>
            ))}
          </div>
          {form.roles.length === 0 && <div className="text-[10px] text-amber-700 mt-1">No roles selected — this employee will have no required competencies.</div>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="bc-label block mb-1">System role</label>
            <select
              value={form.system_role}
              onChange={(e) => setForm({ ...form, system_role: e.target.value })}
              className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm"
              data-testid="edit-emp-system-role"
            >
              <option value="employee">Employee</option>
              <option value="hr">HR</option>
              <option value="quality">Quality</option>
              <option value="management">Management</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>
          <div>
            <label className="bc-label block mb-1">Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm"
              data-testid="edit-emp-status"
            >
              <option value="active">Active</option>
              <option value="on_leave">On Leave</option>
              <option value="resigned">Resigned</option>
            </select>
          </div>
        </div>
        <div>
          <label className="bc-label block mb-1">Workforce Type</label>
          <select
            value={form.workforce_type}
            onChange={(e) => setForm({ ...form, workforce_type: e.target.value })}
            className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm"
            data-testid="edit-emp-workforce-type"
          >
            <option value="staff">Staff</option>
            <option value="worker">Operator</option>
          </select>
          <div className="text-[10px] text-[#666666] mt-1">Controls which navigation track this employee appears under. Auto-set on creation from department.</div>
        </div>
        {err && <div className="text-sm text-[#C41E1E] bg-red-50 px-3 py-2 rounded-sm border border-red-200">{err}</div>}
        <button
          type="submit"
          disabled={busy}
          data-testid="submit-edit-employee"
          className="w-full bg-[#C41E1E] hover:bg-[#A31818] text-white text-sm rounded-sm py-2.5 font-medium disabled:opacity-60"
        >
          {busy ? "Saving…" : "Save changes"}
        </button>
      </form>
    </div>
  );
}


function ResetPasswordModal({ employee, onClose }) {
  const [newPassword, setNewPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const trimmed = newPassword.trim();
    if (trimmed.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/employees/${employee.employee_id}/reset-password`, { new_password: trimmed });
      toast.success(`Password reset for ${employee.name}. Please communicate the new password to them directly.`);
      onClose();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Could not reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      data-testid="reset-password-modal"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-md rounded-sm shadow-2xl p-6 space-y-4"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="bc-label">Reset Password</div>
            <div className="font-heading text-lg font-semibold mt-0.5" data-testid="reset-password-employee-name">
              {employee.name}
            </div>
            <div className="text-xs text-[#666666]">#{employee.employee_id} · {employee.email}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-sm hover:bg-neutral-100" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="text-xs bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-amber-900">
          The new password is shown in plain text below. Communicate it to the employee directly (verbally or WhatsApp).
          No email is sent.
        </div>

        <div>
          <label className="bc-label block mb-1">New Password</label>
          <input
            type="text"
            autoFocus
            value={newPassword}
            onChange={(ev) => setNewPassword(ev.target.value)}
            data-testid="reset-password-input"
            placeholder="Minimum 6 characters"
            className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#0D0D0D]"
          />
          <div className="text-[11px] text-[#666666] mt-1">Minimum 6 characters. Visible on purpose so HR can copy it.</div>
        </div>

        {err && (
          <div className="text-sm text-[#C41E1E] bg-red-50 px-3 py-2 rounded-sm border border-red-200" data-testid="reset-password-error">
            {err}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            data-testid="reset-password-cancel"
            className="text-sm text-[#666666] hover:text-[#0D0D0D] px-3 py-2 rounded-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="reset-password-confirm"
            className="bg-[#C41E1E] hover:bg-[#A31818] text-white text-sm rounded-sm px-4 py-2 font-medium disabled:opacity-60"
          >
            {busy ? "Resetting…" : "Confirm"}
          </button>
        </div>
      </form>
    </div>
  );
}

function DeleteEmployeeModal({ employee, onClose, onDone }) {
  const [preview, setPreview] = useState({ id: null, data: null, err: "" });
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/employees/${employee.employee_id}/delete-preview`)
      .then((r) => {
        if (!cancelled) setPreview({ id: employee.employee_id, data: r.data, err: "" });
      })
      .catch((ex) => {
        if (!cancelled) {
          setPreview({
            id: employee.employee_id,
            data: null,
            err: ex.response?.data?.detail || "Failed to load impact preview",
          });
        }
      });
    return () => { cancelled = true; };
  }, [employee.employee_id]);

  const previewData = preview.id === employee.employee_id ? preview.data : null;
  const previewErr = preview.id === employee.employee_id ? preview.err : "";

  const nameMatches = typed.trim().toLowerCase() === (employee.name || "").trim().toLowerCase() && typed.length > 0;

  const submit = async () => {
    if (!nameMatches) return;
    setBusy(true);
    setErr("");
    try {
      await api.delete(`/employees/${employee.employee_id}`, { data: { confirm_name: typed.trim() } });
      toast.success(`${employee.name} and all linked records deleted permanently.`);
      onDone();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const impact = previewData?.impact;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      data-testid="delete-employee-modal"
      onClick={busy ? undefined : onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-lg rounded-sm shadow-2xl overflow-hidden"
      >
        {/* Red header bar */}
        <div className="bg-[#C41E1E] text-white px-5 py-3 flex items-center gap-2">
          <AlertTriangle size={18} />
          <div className="font-heading text-base font-semibold">Permanently Delete Employee</div>
          {!busy && (
            <button
              type="button"
              onClick={onClose}
              data-testid="delete-employee-close"
              className="ml-auto p-1 rounded-sm hover:bg-white/10"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          {/* Employee identity */}
          <div className="border border-[#E5E5E5] rounded-sm px-4 py-3 bg-[#F9F9F9]">
            <div className="bc-label">Employee</div>
            <div className="font-semibold" data-testid="delete-employee-name">
              {employee.name} <span className="text-[#666666] font-normal">· #{employee.employee_id}</span>
            </div>
            <div className="text-xs text-[#666666]">{employee.department} · {(employee.roles || []).join(", ") || "—"}</div>
          </div>

          {/* Impact counts */}
          {previewErr && <div className="text-sm text-[#C41E1E]">{previewErr}</div>}
          {!previewData && !previewErr && (
            <div className="text-sm text-[#666666]">Loading impact preview…</div>
          )}
          {impact && (
            <div className="border border-[#E5E5E5] rounded-sm divide-y divide-[#E5E5E5]" data-testid="delete-employee-impact">
              <ImpactRow label="Assessment records to be deleted"     value={impact.assessments}          testid="impact-assessments" />
              <ImpactRow label="Pending tests to be cancelled"        value={impact.pending_tests}        testid="impact-pending" />
              <ImpactRow label="Training-attendance records affected" value={impact.attendance_records}   testid="impact-attendance" />
              <ImpactRow label="Training-calendar nominations"        value={impact.calendar_nominations} testid="impact-nominations" />
              <ImpactRow label="Open training needs"                  value={impact.training_needs}       testid="impact-needs" />
            </div>
          )}

          {/* Warning */}
          <div className="bg-red-50 border border-red-200 rounded-sm px-3 py-2.5 text-sm text-[#8B0000]" data-testid="delete-employee-warning">
            This action is permanent and cannot be undone. All records will be deleted. This should only be used for employees who have left the company.
          </div>

          {/* Confirm-by-typing */}
          <div>
            <label className="bc-label block mb-1">
              To confirm, type <span className="font-mono font-semibold text-[#0D0D0D]">{employee.name}</span> exactly
            </label>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              disabled={busy}
              data-testid="delete-employee-confirm-input"
              placeholder={employee.name}
              className={`w-full border rounded-sm px-3 py-2 text-sm focus:outline-none ${
                typed.length > 0 && !nameMatches ? "border-[#C41E1E]" : "border-[#E5E5E5] focus:border-[#0D0D0D]"
              }`}
            />
          </div>

          {err && (
            <div className="text-sm text-[#C41E1E] bg-red-50 px-3 py-2 rounded-sm border border-red-200" data-testid="delete-employee-error">
              {err}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              data-testid="delete-employee-cancel"
              className="text-sm text-[#666666] hover:text-[#0D0D0D] px-3 py-2 rounded-sm disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!nameMatches || busy || !previewData}
              data-testid="delete-employee-confirm"
              className="bg-[#C41E1E] hover:bg-[#A31818] text-white text-sm rounded-sm px-4 py-2 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? "Deleting…" : "Permanently Delete"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ImpactRow({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between px-4 py-2 text-sm">
      <span className="text-[#666666]">{label}</span>
      <span className="font-mono font-semibold text-[#0D0D0D]" data-testid={testid}>{value}</span>
    </div>
  );
}


function PostAddAssignPrompt({ employee, onClose }) {
  const [cmap, setCmap] = useState(null); // list of {role, skills:{code:level}} — null while loading
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/competency-map").then((r) => {
      if (!cancelled) setCmap(r.data);
    }).catch(() => {
      if (!cancelled) setCmap([]);
    });
    return () => { cancelled = true; };
  }, []);

  // Unique skill codes across all the employee's roles.
  const skillCodes = useMemo(() => {
    if (!cmap) return [];
    const roles = new Set(employee.roles || []);
    const codes = new Set();
    for (const c of cmap) {
      if (roles.has(c.role)) {
        for (const sk of Object.keys(c.skills || {})) codes.add(sk);
      }
    }
    return Array.from(codes);
  }, [cmap, employee.roles]);

  const dueDate = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);

  const assignNow = async () => {
    setBusy(true);
    let created = 0;
    let skipped = 0;
    try {
      // Assignment endpoint is per-skill. Fire them in parallel to keep it snappy.
      const results = await Promise.all(
        skillCodes.map((skill_code) =>
          api.post("/assessments/assign", {
            employee_ids: [employee.employee_id],
            skill_code,
            type: "TNI",
            due_date: dueDate,
          }).then((r) => r.data).catch(() => null)
        )
      );
      for (const r of results) {
        if (!r) continue;
        created += (r.created || []).length;
        skipped += (r.skipped_pending || []).length
                + (r.skipped_recent || []).length
                + (r.skipped_inactive || []).length;
      }
      if (created > 0) {
        const tail = skipped > 0 ? ` (${skipped} skipped as duplicates)` : "";
        toast.success(
          `${created} TNI assessments created for ${employee.name}. They will appear in ${employee.name}'s My Tests page.${tail}`
        );
      } else {
        toast.info(`No new TNI assessments created for ${employee.name} — all skills already have pending or recent tests.`);
      }
      onClose();
    } catch {
      toast.error("Failed to assign one or more TNI tests. Please retry from Assign Test page.");
    } finally {
      setBusy(false);
    }
  };

  const skipNow = () => {
    toast.success("Employee added successfully");
    onClose();
  };

  const loading = cmap === null;
  const nothingToAssign = !loading && skillCodes.length === 0;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      data-testid="post-add-assign-prompt"
      onClick={busy ? undefined : skipNow}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-md rounded-sm shadow-2xl p-6 space-y-5"
      >
        <div>
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mb-3">
            <span className="text-green-700 text-lg">&#10003;</span>
          </div>
          <h3 className="font-heading text-lg font-semibold text-[#0D0D0D]" data-testid="post-add-heading">
            {employee.name} has been added successfully.
          </h3>
          <p className="text-sm text-[#666666] mt-2">
            Would you like to assign TNI assessments for all competencies in their role now?
          </p>
        </div>

        <div className="border border-[#E5E5E5] rounded-sm px-4 py-3 bg-[#F9F9F9] text-xs" data-testid="post-add-summary">
          <div className="grid grid-cols-2 gap-y-1.5">
            <div className="text-[#666666]">Roles</div>
            <div className="text-right font-medium text-[#0D0D0D]">
              {(employee.roles || []).join(", ") || "—"}
            </div>
            <div className="text-[#666666]">Skills to assess</div>
            <div className="text-right font-mono font-semibold text-[#0D0D0D]" data-testid="post-add-skill-count">
              {loading ? "…" : skillCodes.length}
            </div>
            <div className="text-[#666666]">Due date</div>
            <div className="text-right font-mono text-[#0D0D0D]">{dueDate}</div>
          </div>
        </div>

        {nothingToAssign && (
          <div className="text-xs bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-amber-900" data-testid="post-add-empty-note">
            The selected role(s) have no skills mapped in the competency map yet, so there is nothing to assign.
          </div>
        )}

        <div className="flex flex-col gap-2 pt-1">
          <button
            type="button"
            onClick={assignNow}
            disabled={busy || loading || nothingToAssign}
            data-testid="post-add-assign-now"
            className="bg-[#C41E1E] hover:bg-[#A31818] text-white text-sm rounded-sm py-2.5 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "Assigning…" : "Assign TNI Tests Now"}
          </button>
          <button
            type="button"
            onClick={skipNow}
            disabled={busy}
            data-testid="post-add-skip"
            className="border border-[#E5E5E5] hover:border-[#0D0D0D] text-[#0D0D0D] text-sm rounded-sm py-2.5 font-medium disabled:opacity-60"
          >
            I&apos;ll Do This Later
          </button>
        </div>
      </div>
    </div>
  );
}


function WorkforceBadge({ type, testid }) {
  if (!type) return null;
  const isWorker = type === "worker";
  const label = isWorker ? "Operator" : "Staff";
  const cls = isWorker
    ? "bg-amber-50 text-amber-800 border-amber-200"
    : "bg-sky-50 text-sky-800 border-sky-200";
  return (
    <span
      data-testid={testid}
      className={`text-[9px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded-sm border ${cls}`}
    >
      {label}
    </span>
  );
}

